<template>
  <div class="pa-message-set" :class="additionalClass">
    <div class="meta" v-if="hasMessage">
      <div class="character-circle">
        <CharacterIcon :character="message.character" />
      </div>
      <div class="date">{{message.created_at | mutateJPDate}}</div>
      <div class="date date-time">{{message.created_at | mutateJPTime}}</div>
    </div>

    <div class="content">
      <div class="message-balloon">
        <template v-if="isLoading">
          <p class="writing-indicator">......</p>
        </template>

        <template v-else>
          <transition name="pa-simple-fade" @enter="handleEnter">
            <div class="message-main-field" v-if="couldVisibleContent">
              <div v-html="message.content"></div>

              <div class="pa-message-additionals"  v-if="hasAdditional">
                <component
                  :is="additionalComponents[message.additionals]"
                  :parameters="message.additionalsParameters"
                  :isLoading="additionalControl.isLoading"
                  :isDisabled="additionalControl.isDisabled"
                  :serverValidationError="additionalControl.serverError"
                  
                  @onSubmit="handleAdditionalSubmit"
                  @onCancel="handleAdditionalCancel"
                  @handleClickAdditionalMenuButton="handleClickAdditionalMenuButton"
                />
              </div>
            </div>
          </transition>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CharacterIcon from '@/components/Parts/CharacterIcon/CharacterIcon.vue'

import {GetMessage} from '@/graphql/queries/Message.graphql'

import MessageSetAdditionalControlMixins from '@/components/Pages/Office/MessageApp/mixins/MessageSetAdditionalControlMixins.js'

export default {
  props : {
    messageId : {
      default: null
    },
    
    isDisabled : {
      default: false
    },
  },

  mixins : [
    MessageSetAdditionalControlMixins
  ],

  components : {
    CharacterIcon,
  },

  data(){
    return {
      isLoading           : false,
      couldVisibleContent : false,

      skinTheme           : 'default',

      additionalClass     : {
        'pa-message-set-default'  : false,
        'pa-message-set-lilica'   : false
      },
    };
  },

  apollo : {
    message : {
      query: GetMessage,
      variables(){
        this.isLoading = true;

        return {
          id : this.messageId
        }
      },

      async result(){
        this.isLoading = false;
      },

      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    }
  },

  computed : {
    hasMessage(){
      return (typeof this.message !== 'undefined');
    },

    isWriting(){
      if(typeof this.message === 'undefined') return false;
      
      return this.message.isWriting;
    },

    hasAdditional(){
      if(typeof this.message.additionals !== undefined){
        return true;
      }

      return false;
    }
  },

  watch : {
    skinTheme   : {
      handler(value){
        switch(value){
          case 'default':
            this.additionalClass['pa-message-set-default'] = true;
            this.additionalClass['pa-message-set-lilica']  = false;
          break;

          case 'lilica':
            this.additionalClass['pa-message-set-default'] = false;
            this.additionalClass['pa-message-set-lilica']  = true;
          break;
        }
      },
      immediate: true,
    },

    isDisabled  : {
      handler(value){
        if(value){
          this.additionalControl.isDisabled = true;
        }else{
          this.additionalControl.isDisabled = false;
        }
      },
      immediate: true
    },

    message : {
      handler(data){
        if(typeof data === 'undefined') return;

        // lilica's id is 2.
        if(parseInt(data.character.id) === 2){
          this.skinTheme = 'lilica';
        }else{
          // other
          this.skinTheme = 'default';
        }

        this.$nextTick(() => {
          this.$parent.$emit('requestScrollBotom');
        });
      },
      immediate: true,
      deep: true
    },

    hasMessage : {
      handler(){
        this.checkCouldVisibleContent();
      },
      immediate: true
    },

    isLoading : {
      handler(){
        this.checkCouldVisibleContent();
      },
      immediate: true
    }
  },

  methods : {
    handleEnter(){
      this.$nextTick(() => {
        this.$parent.$emit('requestScrollBotom');
      });
    },

    checkCouldVisibleContent(){
      let setValue = false;

      if( 
        (this.hasMessage === true) &&
        (this.isLoading === false)
      ){
        setValue = true;
      }

      this.$nextTick(() => {
        this.couldVisibleContent = setValue;
      });
    }
  }
}
</script>

<style lang="scss">
@import "~bulma/sass/utilities/_all";

@keyframes fastFade
{
  0%   {
    opacity:0;
  }
  100% {
    opacity:1;
  }
}

.pa-message-set{
  width:98%;
  margin:0 auto;
  display:flex;
  flex-direction: row;
  position:relative;

  .meta{
    width:16%;
    .character-circle{
      width:38px;
      height:38px;
      margin:0 auto;
      line-height: 38px;

      .pa-character-icon{
        background-color: #DDCEB2;
        box-shadow: 0 0 5px #882F53;

        span.as-text{
          line-height:38px;
        }
      }

      @media screen and (min-width: $desktop) {
        width:75px;
        height:75px;
        line-height: 75px;
        font-size:2rem;

        .pa-character-icon{
          span.as-text{
            line-height: 75px;
          }
        }
      }
    }

    .date{
      margin-top:.3rem;
      font-size:.7rem;
      color:#540E23;
      text-align: center;
      margin-bottom:0;
      user-select: none;

      @include until(375px) {
        font-size:.6rem;
      }

      @media screen and (min-width: $desktop) {
        margin-top:8px;
        font-size:.8rem;
      }

      &-time{
        margin-top:0;
      }
    }
  }

  .content{
    margin-left:1%;
    margin-right:1%;
    width:82%;
    position:relative;

    @media screen and (min-width: $desktop) {
      margin-left:0;
      width:83%;
    }

    .message-balloon{
      background-color:#B5E2D2;
      border:2px solid #a0cabb;
      border-top: 1px solid #a0cabb;
      width:100%;
      border-radius: 6px;
      padding:.4rem .2rem .2rem;
      position: relative;

      p{
        font-size:.9rem;
        margin-bottom:.2rem;
        margin-left:.2rem;

        &.mini-text{
          margin-bottom:.3rem;
          padding:.1rem;
          font-size:.75rem;
          text-align: center;
          background-color: #88c9b2;
          color:#355e50;
          border-radius: 4px;
        }
      }

      .writing-indicator{
        animation: fastFade 1s infinite ease-out;
      }
    }
  }

  &-default{
    .meta{
      .character-circle{
        .pa-character-icon{
          background-color: #DDCEB2;
          box-shadow: 0 0 5px #882F53;
        }
      }

      .date{
        color:#540E23;
      }
    }

    .content{
      .message-balloon{
        background-color:#B5E2D2;
        border-color:#a0cabb;

        p{
          &.mini-text{
            background-color: #88c9b2;
            color:#355e50;
          }
        }
      }
    }
  }

  &-lilica{
    .meta{
      .character-circle{
        .pa-character-icon{
          background-color: #DDCEB2;
          box-shadow: 0 0 5px #882F53;
        }
      }

      .date{
        color:#540E23;
      }
    }

    .content{
      .message-balloon{
        background-color:#DEB6E0;
        border-color:#dbbbdd;

        p{
          &.mini-text{
            color:#7c4980;
            background-color:#d7a1da;
          }
        }
      }
    }
  }
}

</style>